import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Card,
  CardContent,
  Grid,
  Slider,
  Alert,
  CircularProgress,
  Tooltip,
  Avatar,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Tab,
  Tabs,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Campaign as CampaignIcon,
  Create as CreateIcon,
  TrendingUp as EngagementIcon,
  Send as SendIcon,
  ContentCopy as ContentCopyIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Facebook as FacebookIcon,
  MusicNote as TikTokIcon, // Using MusicNote as TikTok alternative
  Settings as SettingsIcon,
  ViewList as OutputIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { SocialMediaService } from '../services/socialMediaService';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';
import { AssetRetrievalService } from '../services/assetRetrievalService';
import {
  SocialMediaSession,
  SocialMediaIteration,
  SocialPlatform,
  SocialAgentType,
  SocialPost,
  PlatformPosts
} from '../models/socialMedia';

// TabPanel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

// Helper function to get platform icons
const getPlatformIcon = (platform: SocialPlatform) => {
  switch (platform) {
    case 'Instagram':
      return <InstagramIcon />;
    case 'Twitter':
      return <TwitterIcon />;
    case 'LinkedIn':
      return <LinkedInIcon />;
    case 'Facebook':
      return <FacebookIcon />;
    case 'TikTok':
      return <TikTokIcon />;
    default:
      return <CampaignIcon />;
  }
};

const SocialMediaIterator: React.FC = () => {
  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch persisted sessions and assets from backend
  const {
    sessions: backendSessions,
    assets: backendAssets,
    loading: backendLoading,
    error: backendError,
    refetch: refetchBackendData
  } = useAssetRetrieval({
    agentType: 'MultiSocialSession',  // Fixed: Use correct SessionType for multi-agent social
    sessionBased: true,
    autoFetch: true,
  });

  // Main state
  const [tabValue, setTabValue] = useState(0);
  const [sessions, setSessions] = useState<SocialMediaSession[]>([]);
  const [currentSession, setCurrentSession] = useState<SocialMediaSession | null>(null);

  // New session creation fields
  const [newSessionTitle, setNewSessionTitle] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [targetPlatforms, setTargetPlatforms] = useState<SocialPlatform[]>(['Instagram', 'Twitter']);
  const [targetAudience, setTargetAudience] = useState('');
  const [brandVoice, setBrandVoice] = useState('');
  const [callToAction, setCallToAction] = useState('');
  const [temperature, setTemperature] = useState(0.7);

  // Current session iteration fields
  const [selectedAgentType, setSelectedAgentType] = useState<SocialAgentType>('StrategyAgent');
  const [additionalInstructions, setAdditionalInstructions] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPlatformTab, setCurrentPlatformTab] = useState(0);
  const [currentPostsPlatformTab, setCurrentPostsPlatformTab] = useState(0);

  // Dialogs
  const [newSessionDialog, setNewSessionDialog] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedPostForPreview, setSelectedPostForPreview] = useState<SocialPost | null>(null);

  const platformIcons: Record<SocialPlatform, React.ReactNode> = {
    Instagram: <InstagramIcon />,
    Twitter: <TwitterIcon />,
    LinkedIn: <LinkedInIcon />,
    Facebook: <FacebookIcon />,
    TikTok: <TikTokIcon />,
  };

  const agentDescriptions: Record<SocialAgentType, string> = {
    StrategyAgent: 'Develops comprehensive campaign strategies and content calendars',
    ContentCreator: 'Creates platform-specific posts optimized for each social network',
    EngagementOptimizer: 'Enhances posts for maximum reach and engagement'
  };

  // Load and merge sessions from localStorage and backend
  useEffect(() => {
    // Load from localStorage
    const localSessions = SocialMediaService.loadSessions();

    // Merge with backend sessions
    if (backendSessions && backendSessions.length > 0) {
      // Convert backend sessions to SocialMediaSession format
      const convertedBackendSessions: SocialMediaSession[] = backendSessions.map(bs => {
        // Parse sessionData JSON string to extract campaign data
        let sessionData: any = {};
        try {
          if (bs.session.sessionData) {
            sessionData = JSON.parse(bs.session.sessionData);
          }
        } catch (e) {
          console.error('[SocialMediaIterator] Failed to parse sessionData:', e);
        }

        // Convert PlatformFinalPosts (strings) to SocialPost objects
        const platformFinalPosts = sessionData.PlatformFinalPosts || bs.session.metadata?.generatedPosts || {};
        const convertedPosts: PlatformPosts = {};

        Object.keys(platformFinalPosts).forEach(platform => {
          const postsForPlatform = platformFinalPosts[platform];
          if (Array.isArray(postsForPlatform)) {
            convertedPosts[platform] = postsForPlatform.map((postContent: string) => {
              // Extract hashtags from post content
              const hashtagMatches = postContent.match(/#\w+/g) || [];
              const hashtags = hashtagMatches.map(h => h.substring(1)); // Remove # prefix

              return {
                platform: platform as SocialPlatform,
                content: postContent,
                hashtags: hashtags,
                characterCount: postContent.length,
              };
            });
          }
        });

        return {
          id: bs.session.id,
          backendSessionId: bs.session.id,
          title: sessionData.Campaign || bs.session.metadata?.title || 'Untitled Campaign',
          campaignDescription: sessionData.Campaign || bs.session.metadata?.campaignDescription || bs.session.metadata?.description || '',
          targetPlatforms: sessionData.Platforms || bs.session.metadata?.targetPlatforms || bs.session.metadata?.platforms || ['Instagram', 'Twitter'],
          targetAudience: sessionData.TargetAudience || bs.session.metadata?.targetAudience || '',
          brandVoice: sessionData.BrandVoice || bs.session.metadata?.brandVoice || '',
          currentStrategy: sessionData.CampaignStrategy || bs.session.metadata?.currentStrategy,
          generatedPosts: convertedPosts,
          iterations: (sessionData.Steps || []).map((step: any, index: number) => ({
            iterationNumber: step.StepNumber || index + 1,
            agentType: step.AgentType || 'unknown',
            input: step.StepInput || '',
            output: step.StepOutput || '',
            timestamp: step.Timestamp || bs.session.createdAt,
          })),
          createdAt: new Date(bs.session.createdAt),
          lastUpdatedAt: new Date(bs.session.lastUpdatedAt || bs.session.updatedAt || bs.session.createdAt),
        };
      });

      // Merge: prefer backend sessions (they're authoritative)
      const sessionMap = new Map<string, SocialMediaSession>();

      // Add local sessions first
      localSessions.forEach(s => sessionMap.set(s.id, s));

      // Override with backend sessions
      convertedBackendSessions.forEach(s => sessionMap.set(s.id, s));

      const mergedSessions = Array.from(sessionMap.values());
      setSessions(mergedSessions);

      // Only set current session if none is selected
      if (mergedSessions.length > 0 && !currentSession) {
        setCurrentSession(mergedSessions[0]);
      }
    } else {
      setSessions(localSessions);
      // Only set current session if none is selected
      if (localSessions.length > 0 && !currentSession) {
        setCurrentSession(localSessions[0]);
      }
    }
  }, [backendSessions]); // Removed refetchBackendData() call and currentSession dependency

  const validateSessionForm = (): string | null => {
    if (!newSessionTitle.trim()) return 'Campaign title is required';
    if (!campaignDescription.trim()) return 'Campaign description is required';
    if (targetPlatforms.length === 0) return 'At least one platform is required';
    if (!targetAudience.trim()) return 'Target audience is required';
    if (!brandVoice.trim()) return 'Brand voice is required';
    return null;
  };

  const createNewSession = async () => {
    const validation = validateSessionForm();
    if (validation) {
      setError(validation);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await SocialMediaService.startMultiAgentSession({
        campaignDescription,
        targetPlatforms,
        targetAudience,
        brandVoice,
        postCountPerPlatform: 3,
        callToAction
      });

      // Convert string arrays to SocialPost objects
      const convertedPosts: { [platform: string]: SocialPost[] } = {};
      if (response.platformPosts) {
        Object.entries(response.platformPosts).forEach(([platform, posts]) => {
          convertedPosts[platform] = (posts as string[]).map((content, index) => ({
            platform: platform as SocialPlatform,
            content,
            hashtags: response.hashtagSuggestions || [],
            characterCount: content.length,
            mediaType: 'text' as const
          }));
        });
      }
      
      console.log('Converted posts:', convertedPosts); // Debug log

      const session: SocialMediaSession = {
        id: `social-${Date.now()}`,
        backendSessionId: response.sessionId,
        title: newSessionTitle,
        campaignDescription,
        targetPlatforms,
        targetAudience,
        brandVoice,
        currentStrategy: response.campaignStrategy,
        createdAt: new Date(),
        lastUpdatedAt: new Date(),
        iterations: [{
          iterationNumber: 0,
          agentType: 'StrategyAgent',
          input: campaignDescription,
          output: response.campaignStrategy,
          timestamp: new Date(),
          hashtags: response.hashtagSuggestions
        }],
        generatedPosts: convertedPosts
      };

      SocialMediaService.saveSession(session);
      setCurrentSession(session);
      setSessions([session, ...sessions]);

      // Clear form
      setNewSessionTitle('');
      setCampaignDescription('');
      setTargetAudience('');
      setBrandVoice('');
      setCallToAction('');
      setNewSessionDialog(false);
      setTabValue(2); // Switch to current session tab
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const generateIteration = async () => {
    if (!currentSession) return;

    setLoading(true);
    setError(null);

    try {
      const response = await SocialMediaService.iterateSession(currentSession.backendSessionId || currentSession.id, {
        agentType: selectedAgentType,
        userInput: additionalInstructions || `Continue developing the ${selectedAgentType}`,
        additionalInstructions,
        targetPlatforms: currentSession.targetPlatforms,
        temperature
      });

      const newIteration: SocialMediaIteration = {
        iterationNumber: currentSession.iterations.length,
        agentType: selectedAgentType,
        input: additionalInstructions || `Continue developing the ${selectedAgentType}`,
        output: response.output || 'Iteration completed',
        timestamp: new Date(),
        userInstruction: additionalInstructions || undefined,
        isUserAction: false
      };

      // Convert string arrays to SocialPost objects for iteration response
      const convertedIterationPosts: { [platform: string]: SocialPost[] } = {};
      if (response.platformPosts) {
        Object.entries(response.platformPosts).forEach(([platform, posts]) => {
          convertedIterationPosts[platform] = (posts as string[]).map((content, index) => ({
            platform: platform as SocialPlatform,
            content,
            hashtags: response.hashtags || [],
            characterCount: content.length,
            mediaType: 'text' as const
          }));
        });
      }

      const updatedSession: SocialMediaSession = {
        ...currentSession,
        lastUpdatedAt: new Date(),
        iterations: [...currentSession.iterations, newIteration],
        generatedPosts: { ...currentSession.generatedPosts, ...convertedIterationPosts }
      };

      SocialMediaService.updateSession(updatedSession);
      setCurrentSession(updatedSession);
      setSessions(sessions.map(s => s.id === updatedSession.id ? updatedSession : s));

      // Clear form
      setAdditionalInstructions('');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to generate iteration');
    } finally {
      setLoading(false);
    }
  };

  const deleteSession = (sessionId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    SocialMediaService.deleteSession(sessionId);
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
    }
    refetchBackendData();
  };

  const exportSession = () => {
    if (currentSession) {
      SocialMediaService.exportSession(currentSession);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await SocialMediaService.copyToClipboard(text);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  interface PostCardProps {
    post: SocialPost;
  }

  const PostCard = ({ post }: PostCardProps) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Chip 
            icon={getPlatformIcon(post.platform)} 
            label={post.platform}
            size="small"
            color="primary"
          />
          <Typography variant="caption" color="text.secondary">
            {post.characterCount} characters
          </Typography>
        </Box>
        
        <Box sx={{ 
          '& p': { margin: '4px 0', fontSize: '0.875rem' }, 
          '& h1, & h2, & h3, & h4, & h5, & h6': { margin: '6px 0', fontSize: '1rem', fontWeight: 600 },
          '& ul, & ol': { margin: '4px 0', paddingLeft: '20px' },
          '& li': { margin: '2px 0' }
        }}>
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </Box>

        {post.hashtags && post.hashtags.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {post.hashtags.map((hashtag, i) => (
              <Chip key={i} label={hashtag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Tooltip title="Copy post content">
            <IconButton size="small" onClick={() => copyToClipboard(post.content)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            flexWrap: 'wrap',
            gap: { xs: 0.5, sm: 1 }, 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' },
          }}
        >
          <CampaignIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          Social Media Campaign Studio
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 1, sm: 0 },
          }}
        >
          Create engaging social media campaigns with AI-powered agents
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }} elevation={0}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minWidth: { xs: 90, sm: 120 },
              minHeight: { xs: 44, sm: 48 },
            }
          }}
        >
          <Tab label="Sessions" />
          <Tab label="Create New" />
          {currentSession && <Tab label="Current Campaign" />}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        {/* Sessions List */}
        {sessions.length === 0 ? (
          <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              gutterBottom
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              No campaigns found
            </Typography>
            <Button
              variant="contained"
              startIcon={!isMobile && <AddIcon />}
              onClick={() => setTabValue(1)}
              size={isMobile ? 'medium' : 'large'}
              fullWidth={isMobile}
            >
              Create Your First Campaign
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 2 }}>
            {sessions.map((session) => (
              <Grid item xs={12} md={6} key={session.id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: currentSession?.id === session.id ? 2 : 1,
                    borderColor: currentSession?.id === session.id ? 'primary.main' : 'divider'
                  }}
                  onClick={() => {
                    setCurrentSession(session);
                    setTabValue(2);
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start', 
                      mb: { xs: 1, sm: 1 },
                      gap: 1,
                    }}>
                      <Typography 
                        variant="h6"
                        sx={{ 
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          lineHeight: 1.3,
                        }}
                      >
                        {session.title}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => deleteSession(session.id, e)}
                        color="error"
                        sx={{
                          minWidth: 44,
                          minHeight: 44,
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    >
                      Platforms: {session.targetPlatforms.join(', ')}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                    >
                      Created: {new Date(session.createdAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: isMobile ? '2-digit' : 'numeric'
                      })} • {session.iterations.length} iteration{session.iterations.length !== 1 ? 's' : ''}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Create New Campaign */}
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
          >
            Create New Campaign
          </Typography>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: { xs: 1.5, sm: 2 },
                py: { xs: 0.5, sm: 1 },
              }} 
              onClose={() => setError(null)}
            >
              <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {error}
              </Typography>
            </Alert>
          )}

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Campaign Title *"
                value={newSessionTitle}
                onChange={(e) => setNewSessionTitle(e.target.value)}
                placeholder="e.g., Spring Product Launch Campaign"
                error={!newSessionTitle.trim()}
                helperText={!newSessionTitle.trim() ? "Campaign title is required" : ""}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 3 : 4}
                label="Campaign Description *"
                value={campaignDescription}
                onChange={(e) => setCampaignDescription(e.target.value)}
                placeholder="Describe your campaign goals, key messages, and specific requirements..."
                error={!campaignDescription.trim()}
                helperText={!campaignDescription.trim() ? "Campaign description is required" : "Describe your campaign goals and key messages"}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Target Audience *"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                placeholder="e.g., Young professionals aged 25-35"
                error={!targetAudience.trim()}
                helperText={!targetAudience.trim() ? "Target audience is required" : ""}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Brand Voice *"
                value={brandVoice}
                onChange={(e) => setBrandVoice(e.target.value)}
                placeholder="e.g., Friendly, professional, inspiring"
                error={!brandVoice.trim()}
                helperText={!brandVoice.trim() ? "Brand voice is required" : ""}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Call to Action"
                value={callToAction}
                onChange={(e) => setCallToAction(e.target.value)}
                placeholder="e.g., Visit our website, Download our app"
                helperText="Optional: Specific action you want users to take"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography 
                variant="subtitle2" 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Target Platforms *
              </Typography>
              <FormGroup row={!isMobile}>
                {(['Instagram', 'Twitter', 'LinkedIn', 'Facebook', 'TikTok'] as SocialPlatform[]).map((platform) => (
                  <FormControlLabel
                    key={platform}
                    control={
                      <Checkbox
                        checked={targetPlatforms.includes(platform)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setTargetPlatforms([...targetPlatforms, platform]);
                          } else {
                            setTargetPlatforms(targetPlatforms.filter(p => p !== platform));
                          }
                        }}
                        size={isMobile ? 'small' : 'medium'}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.5 } }}>
                        {getPlatformIcon(platform)}
                        <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          {platform}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
              {targetPlatforms.length === 0 && (
                <Typography 
                  variant="caption" 
                  color="error"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                >
                  Please select at least one platform
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
                <Typography 
                  variant="body2" 
                  gutterBottom
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  AI Creativity Level: {temperature}
                </Typography>
                <Slider
                  value={temperature}
                  onChange={(_, value) => setTemperature(value as number)}
                  min={0}
                  max={1}
                  step={0.1}
                  marks={isMobile ? undefined : [
                    { value: 0, label: 'Conservative' },
                    { value: 0.5, label: 'Balanced' },
                    { value: 1, label: 'Creative' }
                  ]}
                  valueLabelDisplay="auto"
                  sx={{ 
                    mx: { xs: 1, sm: 2 },
                    '& .MuiSlider-thumb': {
                      width: { xs: 20, sm: 24 },
                      height: { xs: 20, sm: 24 },
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Button
            fullWidth
            size={isMobile ? 'medium' : 'large'}
            variant="contained"
            onClick={createNewSession}
            disabled={loading || validateSessionForm() !== null}
            startIcon={loading ? <CircularProgress size={20} /> : (isMobile ? null : <AddIcon />)}
            sx={{ mt: { xs: 2, sm: 3 } }}
          >
            {loading ? 'Creating...' : (isMobile ? 'Create Campaign' : 'Create Social Media Campaign')}
          </Button>
        </Paper>
      </TabPanel>

      {currentSession && (
        <TabPanel value={tabValue} index={2}>
          {/* Current Campaign Tabs */}
          <Paper sx={{ mb: { xs: 2, sm: 3 } }} elevation={0}>
            <Tabs 
              value={currentPlatformTab} 
              onChange={(_, newValue) => setCurrentPlatformTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  minHeight: { xs: 44, sm: 48 },
                }
              }}
            >
              <Tab 
                icon={<SettingsIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} 
                label="Settings" 
                iconPosition="start"
              />
              <Tab 
                icon={<OutputIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} 
                label="Campaign Output" 
                iconPosition="start"
              />
            </Tabs>
          </Paper>

          <TabPanel value={currentPlatformTab} index={0}>
            {/* Settings Tab */}
            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                mb: { xs: 2, sm: 3 },
                gap: { xs: 1, sm: 0 },
              }}>
                <Typography 
                  variant="h5"
                  sx={{ 
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    lineHeight: 1.3,
                  }}
                >
                  {currentSession.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Export Campaign">
                    <IconButton 
                      onClick={exportSession}
                      sx={{
                        minWidth: 44,
                        minHeight: 44,
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Campaign Information */}
              <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Campaign Description
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {currentSession.campaignDescription}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Target Audience
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {currentSession.targetAudience}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Brand Voice
                      </Typography>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {currentSession.brandVoice}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                      >
                        Target Platforms
                      </Typography>
                      <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 0.5 }, flexWrap: 'wrap', mt: 1 }}>
                        {currentSession.targetPlatforms.map((platform) => (
                          <Chip 
                            key={platform} 
                            icon={getPlatformIcon(platform)} 
                            label={platform} 
                            size={isMobile ? 'small' : 'small'}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Generation Controls */}
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
              >
                Generate Iteration
              </Typography>
              
              <Grid container spacing={{ xs: 2, sm: 2 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Agent Type</InputLabel>
                    <Select
                      value={selectedAgentType}
                      onChange={(e) => setSelectedAgentType(e.target.value as SocialAgentType)}
                      label="Agent Type"
                    >
                      {Object.entries(agentDescriptions).map(([agent, description]) => (
                        <MenuItem key={agent} value={agent}>
                          <Box>
                            <Typography 
                              variant="body2"
                              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            >
                              {agent.replace('Agent', ' Agent')}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            >
                              {description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={isMobile ? 2 : 3}
                    label="Additional Instructions"
                    value={additionalInstructions}
                    onChange={(e) => setAdditionalInstructions(e.target.value)}
                    placeholder="Provide specific instructions for this iteration..."
                    helperText="Optional: Provide specific guidance for the AI agent"
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
                    <Typography 
                      variant="body2" 
                      gutterBottom
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      Temperature: {temperature}
                    </Typography>
                    <Slider
                      value={temperature}
                      onChange={(_, value) => setTemperature(value as number)}
                      min={0}
                      max={1}
                      step={0.1}
                      marks={isMobile ? undefined : [
                        { value: 0, label: 'Precise' },
                        { value: 0.5, label: 'Balanced' },
                        { value: 1, label: 'Creative' }
                      ]}
                      valueLabelDisplay="auto"
                      sx={{ 
                        mx: { xs: 1, sm: 2 },
                        '& .MuiSlider-thumb': {
                          width: { xs: 20, sm: 24 },
                          height: { xs: 20, sm: 24 },
                        },
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    size={isMobile ? 'medium' : 'large'}
                    variant="contained"
                    onClick={generateIteration}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : (isMobile ? null : <SendIcon />)}
                  >
                    {loading ? 'Generating...' : (isMobile ? 'Generate' : `Generate with ${selectedAgentType.replace('Agent', ' Agent')}`)}
                  </Button>
                </Grid>
              </Grid>

              {/* Error Display */}
              {error && (
                <Alert 
                  severity="error" 
                  onClose={() => setError(null)} 
                  sx={{ 
                    mt: { xs: 1.5, sm: 2 },
                    py: { xs: 0.5, sm: 1 },
                  }}
                >
                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {error}
                  </Typography>
                </Alert>
              )}

              {/* Iteration History */}
              {currentSession.iterations.length > 0 && (
                <Box sx={{ mt: { xs: 3, sm: 4 } }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                  >
                    Campaign Development History
                  </Typography>
                  <Stack spacing={{ xs: 1.5, sm: 2 }}>
                    {currentSession.iterations.map((iteration, index) => (
                      <Card key={index} variant="outlined">
                        <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'flex-start', sm: 'flex-start' }, 
                            mb: { xs: 1, sm: 1 },
                            gap: { xs: 1, sm: 0 },
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, flexWrap: 'wrap' }}>
                              <Avatar sx={{ 
                                bgcolor: 'primary.main', 
                                width: { xs: 20, sm: 24 }, 
                                height: { xs: 20, sm: 24 },
                              }}>
                                <Typography 
                                  variant="caption"
                                  sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                >
                                  {iteration.iterationNumber}
                                </Typography>
                              </Avatar>
                              <Typography 
                                variant="subtitle2"
                                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                              >
                                {iteration.agentType.replace('Agent', ' Agent')}
                              </Typography>
                            </Box>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: { xs: 0.5, sm: 1 },
                              width: { xs: '100%', sm: 'auto' },
                              justifyContent: { xs: 'space-between', sm: 'flex-end' },
                            }}>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                              >
                                {iteration.timestamp.toLocaleString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                              <Tooltip title="Copy to clipboard">
                                <IconButton 
                                  size="small" 
                                  onClick={() => copyToClipboard(iteration.output)}
                                  sx={{
                                    minWidth: 44,
                                    minHeight: 44,
                                  }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                          {iteration.userInstruction && (
                            <Alert 
                              severity="info" 
                              sx={{ 
                                mb: { xs: 1.5, sm: 2 },
                                py: { xs: 0.5, sm: 1 },
                              }}
                            >
                              <Typography 
                                variant="body2"
                                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                              >
                                <strong>User instruction:</strong> {iteration.userInstruction}
                              </Typography>
                            </Alert>
                          )}
                          {iteration.hashtags && iteration.hashtags.length > 0 && (
                            <Box sx={{ mb: { xs: 1, sm: 1 } }}>
                              {iteration.hashtags.map((hashtag, i) => (
                                <Chip 
                                  key={i} 
                                  label={hashtag} 
                                  size="small" 
                                  sx={{ 
                                    mr: { xs: 0.5, sm: 0.5 }, 
                                    mb: { xs: 0.5, sm: 0 },
                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  }} 
                                />
                              ))}
                            </Box>
                          )}
                          <Paper sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'primary.50' }}>
                            <Box sx={{ 
                              '& p': { margin: '4px 0', fontSize: { xs: '0.8rem', sm: '0.875rem' } }, 
                              '& h1, & h2, & h3, & h4, & h5, & h6': { margin: '6px 0', fontSize: { xs: '0.9rem', sm: '1rem' }, fontWeight: 600 },
                              '& ul, & ol': { margin: '4px 0', paddingLeft: { xs: '16px', sm: '20px' } },
                              '& li': { margin: '2px 0', fontSize: { xs: '0.8rem', sm: '0.875rem' } }
                            }}>
                              <ReactMarkdown>{iteration.output}</ReactMarkdown>
                            </Box>
                          </Paper>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              )}
            </Paper>
          </TabPanel>

          <TabPanel value={currentPlatformTab} index={1}>
            {/* Campaign Output */}
            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
              >
                Generated Posts
              </Typography>
              
              {Object.keys(currentSession.generatedPosts).length === 0 ? (
                <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    No posts generated yet
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    Use the Settings tab to generate content with different agents
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {/* Platform Tabs for Posts */}
                  <Tabs
                    value={currentPostsPlatformTab}
                    onChange={(_, newValue) => setCurrentPostsPlatformTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ 
                      mb: { xs: 2, sm: 3 },
                      '& .MuiTab-root': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        minHeight: { xs: 44, sm: 48 },
                      }
                    }}
                  >
                    {currentSession.targetPlatforms.map((platform, index) => {
                      const posts = currentSession.generatedPosts[platform] || [];
                      return (
                        <Tab
                          key={platform}
                          icon={getPlatformIcon(platform)}
                          label={`${platform} (${posts.length})`}
                          iconPosition="start"
                        />
                      );
                    })}
                  </Tabs>

                  {/* Posts Display - Show only the selected platform */}
                  {(() => {
                    const selectedPlatform = currentSession.targetPlatforms[currentPostsPlatformTab];
                    if (!selectedPlatform) return null;
                    const posts = currentSession.generatedPosts[selectedPlatform] || [];
                    
                    if (posts.length === 0) {
                      return (
                        <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
                          <Typography 
                            variant="body1" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                          >
                            No posts generated for {selectedPlatform} yet
                          </Typography>
                        </Box>
                      );
                    }
                    
                    return (
                      <Box>
                        <Typography 
                          variant="h6" 
                          gutterBottom 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: { xs: 0.5, sm: 1 },
                            fontSize: { xs: '1rem', sm: '1.25rem' },
                          }}
                        >
                          {getPlatformIcon(selectedPlatform)}
                          {selectedPlatform} Posts
                        </Typography>
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                          {posts.map((post, postIndex) => (
                            <Grid item xs={12} md={6} key={postIndex}>
                              <PostCard post={post} />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    );
                  })()}
                </Box>
              )}
            </Paper>
          </TabPanel>
        </TabPanel>
      )}
    </Container>
  );
};

export default SocialMediaIterator;